// Typeahead over places, regions, routes, people and outings — five root
// fields in one GraphQL request. Selecting a place/route with coordinates can
// fly the map; everything links to its entity page. Arrow keys move an active
// highlight through the flat result list; Enter follows the active item.
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "wouter";
import { Compass, Crosshair, LocateFixed, Map, MapPin, User, type LucideIcon } from "lucide-react";
import { gqlRequest } from "../lib/gql";
import { NEARBY, SEARCH, type NearbyResult, type SearchResult } from "../lib/queries";
import { ACTIVITY_ICONS, formatDate, formatDistance, PLACE_TYPE_ICONS, type PlaceType } from "../lib/format";
import { useT } from "../lib/i18n";
import { pointCoords } from "../lib/geo";

type Props = {
  onFlyTo?: (target: { lng: number; lat: number; zoom: number }) => void;
  /** Current map center when the host page has a map; nearby falls back to geolocation without it. */
  getCenter?: () => { lng: number; lat: number } | null;
};

type Origin = { lon: number; lat: number };

/** Great-circle distance in meters — close enough for "1.2 km away" labels. */
function distanceM(origin: Origin, [lon, lat]: [number, number]): number {
  const rad = Math.PI / 180;
  const dLat = (lat - origin.lat) * rad;
  const dLon = (lon - origin.lon) * rad;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(origin.lat * rad) * Math.cos(lat * rad) * Math.sin(dLon / 2) ** 2;
  return 2 * 6371000 * Math.asin(Math.sqrt(a));
}

type Item = {
  key: string;
  href: string;
  icon: LucideIcon;
  title: string;
  subtitle: string;
  fly?: { lng: number; lat: number; zoom: number };
};

type Section = { label: string; items: Item[] };

type I18nFns = ReturnType<typeof useT>;

function toSections(data: SearchResult | undefined, { t, labelizeT, entityName }: I18nFns): Section[] {
  if (!data) return [];
  const sections: Section[] = [
    {
      label: t("search.sectionPlaces"),
      items: data.places.nodes.map((p) => {
        const coords = pointCoords(p.geom);
        return {
          key: `place-${p.id}`,
          href: `/place/${p.id}`,
          icon: PLACE_TYPE_ICONS[p.type as PlaceType] ?? MapPin,
          title: entityName(p),
          subtitle: `${labelizeT(p.type)}${p.elevation ? ` · ${p.elevation} m` : ""}`,
          fly: coords ? { lng: coords[0], lat: coords[1], zoom: 13 } : undefined,
        };
      }),
    },
    {
      label: t("search.sectionRegions"),
      items: data.regions.nodes.map((r) => ({
        key: `region-${r.id}`,
        href: `/region/${r.id}`,
        icon: Map,
        title: entityName(r),
        subtitle: labelizeT(r.type),
      })),
    },
    {
      label: t("search.sectionRoutes"),
      items: data.routes.nodes.map((r) => {
        const coords = pointCoords(r.startPoint);
        return {
          key: `route-${r.id}`,
          href: `/route/${r.id}`,
          icon: Compass,
          title: entityName(r),
          subtitle: r.activities.map(labelizeT).join(", "),
          fly: coords ? { lng: coords[0], lat: coords[1], zoom: 13 } : undefined,
        };
      }),
    },
    {
      label: t("search.sectionPeople"),
      items: data.people.nodes.map((p) => ({
        key: `person-${p.id}`,
        href: `/person/${p.id}`,
        icon: User,
        title: p.displayName ?? t("search.unnamed"),
        subtitle: p.activities?.length ? p.activities.map(labelizeT).join(", ") : t("search.person"),
      })),
    },
    {
      label: t("search.sectionOutings"),
      items: data.outings.nodes.map((o) => ({
        key: `outing-${o.id}`,
        href: `/outing/${o.id}`,
        icon: ACTIVITY_ICONS[o.activity] ?? Compass,
        title: o.title,
        subtitle: `${formatDate(o.date)} · ${labelizeT(o.activity)}`,
      })),
    },
  ];
  return sections.filter((s) => s.items.length > 0);
}

function toNearbySections(data: NearbyResult | undefined, origin: Origin, { t, labelizeT, entityName }: I18nFns): Section[] {
  if (!data) return [];
  const withDistance = (coords: [number, number] | null) =>
    coords ? ` · ${t("search.away", { distance: formatDistance(distanceM(origin, coords)) })}` : "";
  const sections: Section[] = [
    {
      label: t("search.nearbyPlaces"),
      items: data.placesNearby.map((p) => {
        const coords = pointCoords(p.geom);
        return {
          key: `place-${p.id}`,
          href: `/place/${p.id}`,
          icon: PLACE_TYPE_ICONS[p.type as PlaceType] ?? MapPin,
          title: entityName(p),
          subtitle: `${labelizeT(p.type)}${withDistance(coords)}`,
          fly: coords ? { lng: coords[0], lat: coords[1], zoom: 13 } : undefined,
        };
      }),
    },
    {
      label: t("search.nearbyRoutes"),
      items: data.routesNearby.map((r) => {
        const coords = pointCoords(r.startPoint);
        return {
          key: `route-${r.id}`,
          href: `/route/${r.id}`,
          icon: Compass,
          title: entityName(r),
          subtitle: `${r.activities.map(labelizeT).join(", ")}${withDistance(coords)}`,
          fly: coords ? { lng: coords[0], lat: coords[1], zoom: 13 } : undefined,
        };
      }),
    },
  ];
  return sections.filter((s) => s.items.length > 0);
}

export function SearchBox({ onFlyTo, getCenter }: Props) {
  const i18n = useT();
  const { t } = i18n;
  const [term, setTerm] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();
  const [, navigate] = useLocation();

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term.trim()), 250);
    return () => clearTimeout(t);
  }, [term]);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("pointerdown", onDown);
    return () => window.removeEventListener("pointerdown", onDown);
  }, []);

  const [origin, setOrigin] = useState<Origin | null>(null);
  const [geoState, setGeoState] = useState<"idle" | "locating" | "error">("idle");

  const searchMode = debounced.length >= 2;

  const { data, isFetching } = useQuery({
    queryKey: ["search", debounced],
    enabled: searchMode,
    queryFn: () => gqlRequest<SearchResult>(SEARCH, { q: `%${debounced}%` }),
  });

  const nearby = useQuery({
    queryKey: ["nearby", origin],
    enabled: !searchMode && origin != null,
    queryFn: () => gqlRequest<NearbyResult>(NEARBY, { lon: origin!.lon, lat: origin!.lat, maxN: 8 }),
  });

  const locate = () => {
    setGeoState("idle");
    const center = getCenter?.();
    if (center) {
      setOrigin({ lon: center.lng, lat: center.lat });
      return;
    }
    if (!navigator.geolocation) {
      setGeoState("error");
      return;
    }
    setGeoState("locating");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeoState("idle");
        setOrigin({ lon: pos.coords.longitude, lat: pos.coords.latitude });
      },
      () => setGeoState("error"),
    );
  };

  const sections = useMemo(
    () => (searchMode ? toSections(data, i18n) : origin ? toNearbySections(nearby.data, origin, i18n) : []),
    [searchMode, data, origin, nearby.data, i18n],
  );
  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  // New results invalidate the old highlight position.
  useEffect(() => setActive(-1), [flat]);

  useEffect(() => {
    if (active < 0) return;
    document.getElementById(optionId(listId, active))?.scrollIntoView({ block: "nearest" });
  }, [active, listId]);

  // With an empty query the dropdown still opens, offering the "near here" quick action.
  const showList = open;

  const close = () => {
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (open) close();
      else setTerm("");
      return;
    }
    if (!showList || flat.length === 0) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActive((a) => (a + 1) % flat.length);
        break;
      case "ArrowUp":
        e.preventDefault();
        setActive((a) => (a <= 0 ? flat.length - 1 : a - 1));
        break;
      case "Home":
        e.preventDefault();
        setActive(0);
        break;
      case "End":
        e.preventDefault();
        setActive(flat.length - 1);
        break;
      case "Enter": {
        const item = flat[active];
        if (!item) return;
        e.preventDefault();
        close();
        navigate(item.href);
        break;
      }
    }
  };

  let index = -1;

  return (
    <div ref={rootRef} className="relative w-full max-w-xs">
      <input
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        role="combobox"
        aria-expanded={showList}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={active >= 0 ? optionId(listId, active) : undefined}
        placeholder={t("search.placeholder")}
        className="w-full rounded-full border bg-background px-4 py-2 text-base text-foreground shadow-card md:text-sm transition-all duration-150 placeholder:text-muted-foreground hover:border-muted-foreground/40 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      />
      {showList && (
        <div
          id={listId}
          role="listbox"
          className="absolute top-full z-40 mt-1.5 max-h-80 w-full overflow-y-auto rounded-xl border bg-popover text-popover-foreground shadow-pop"
        >
          {!searchMode && (
            <button
              type="button"
              onClick={locate}
              disabled={geoState === "locating"}
              className="flex w-full items-center gap-2.5 border-b px-3 py-2.5 text-left text-sm font-medium text-primary transition-colors hover:bg-accent disabled:opacity-60"
            >
              <LocateFixed aria-hidden className="size-4 shrink-0" />
              {geoState === "locating" ? t("search.locating") : getCenter ? t("search.nearMapCenter") : t("search.nearMe")}
            </button>
          )}
          {!searchMode && geoState === "error" && (
            <p className="p-3.5 text-sm text-destructive">{t("search.geoError")}</p>
          )}
          {!searchMode && origin != null && nearby.isFetching && (
            <p className="p-3.5 text-sm text-muted-foreground">{t("search.findingNearby")}</p>
          )}
          {!searchMode && origin != null && nearby.error != null && (
            <p className="p-3.5 text-sm text-destructive">{t("search.nearbyError")}</p>
          )}
          {!searchMode && origin != null && nearby.data != null && flat.length === 0 && !nearby.isFetching && (
            <p className="p-3.5 text-sm text-muted-foreground">{t("search.nothingNearby")}</p>
          )}
          {searchMode && flat.length === 0 && (
            <p className="p-3.5 text-sm text-muted-foreground">{isFetching ? t("search.searching") : t("search.noMatches")}</p>
          )}
          {sections.map((section) => (
            <div key={section.label}>
              <SectionLabel>{section.label}</SectionLabel>
              {section.items.map((item) => {
                index += 1;
                const i = index;
                return (
                  <ResultRow
                    key={item.key}
                    id={optionId(listId, i)}
                    item={item}
                    active={i === active}
                    onHover={() => setActive(i)}
                    onFly={item.fly && onFlyTo ? () => onFlyTo(item.fly!) : undefined}
                    close={close}
                  />
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function optionId(listId: string, index: number) {
  return `${listId}-opt-${index}`;
}

function SectionLabel({ children }: { children: string }) {
  return (
    <p className="sticky top-0 border-b bg-muted/90 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground backdrop-blur-sm">
      {children}
    </p>
  );
}

function ResultRow({
  id,
  item,
  active,
  onHover,
  onFly,
  close,
}: {
  id: string;
  item: Item;
  active: boolean;
  onHover: () => void;
  onFly?: () => void;
  close: () => void;
}) {
  const { t } = useT();
  const Icon = item.icon;
  return (
    <div
      id={id}
      role="option"
      aria-selected={active}
      onPointerMove={onHover}
      className={`flex items-center gap-1 border-b transition-colors last:border-0 ${active ? "bg-accent" : "hover:bg-accent"}`}
    >
      <Link href={item.href} onClick={close} className="flex min-w-0 flex-1 items-center gap-2.5 px-3 py-2.5">
        <Icon aria-hidden className="size-4 shrink-0 text-muted-foreground" />
        <span className="min-w-0">
          <span className="block truncate text-sm font-medium text-foreground">{item.title}</span>
          <span className="block truncate text-xs text-muted-foreground">{item.subtitle}</span>
        </span>
      </Link>
      {onFly && (
        <button
          type="button"
          title={t("search.showOnMap")}
          onClick={() => {
            onFly();
            close();
          }}
          className="shrink-0 rounded-lg px-2 py-2 text-muted-foreground/50 transition-colors hover:text-primary"
        >
          <Crosshair aria-hidden className="size-4" />
        </button>
      )}
    </div>
  );
}
